import { Edit2, Plus } from "lucide-react";
import { Button, Form, Input, Modal, Select } from "antd";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
  getExecutiveEmployeeList,
  updateLeadDetails,
} from "../../actionCreator/leadListingActionCreater";
import { getUserInfo } from "../../utils/utils";
import { useAlert } from "../../context/AlertProvider";
import {
  formatAndExtractCountryCode,
  handleMobileChange,
  prefixSelector,
} from "../../utils/mobileValidation";
import { countries } from "../../constant/Countries";
import PlaceAutocomplete from "../../common/PlaceAutocomplete";
import { AuthContext } from "../../context/AuthContext";

const { TextArea } = Input;

const UpdateLead = ({ title, defaultValue, fieldName, id, callback }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showAlert } = useAlert();
  const { isLeadEdit } = useContext(AuthContext);

  const showModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isLeadEdit) {
      setIsModalOpen(true);
    } else {
      showAlert(
        "warning",
        "Permission Denied",
        "You do not have permission to update lead.",
      );
    }
  };

  const handleCancel = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsModalOpen(false);
  };

  return (
    <>
      {defaultValue ? (
        <Edit2
          size={16}
          className="edit-field-icon tw-ml-2 tw-cursor-pointer tw-text-slate-600 hover:tw-text-[#5D44FF]"
          onClick={showModal}
        />
      ) : (
        <Plus
          size={16}
          className="edit-field-icon tw-ml-2 tw-cursor-pointer tw-text-slate-600 hover:tw-text-[#5D44FF]"
          onClick={showModal}
        />
      )}
      <Modal
        title={null}
        open={isModalOpen}
        centered
        width={400}
        footer={null}
        className="delete-popup"
        onOk={handleCancel}
        onCancel={handleCancel}
      >
        <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
          Update {title}
        </div>
        <LeadForm
          title={title}
          defaultValue={defaultValue}
          fieldName={fieldName}
          id={id}
          handleCancel={handleCancel}
          callback={callback}
        />
      </Modal>
    </>
  );
};

export default UpdateLead;

const LeadForm = ({
  title,
  defaultValue,
  fieldName,
  id,
  handleCancel,
  callback,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { id: userId } = getUserInfo();
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState();
  const [reason, setReason] = useState();
  const [error, setError] = useState();
  const [isChanged, setIsChanged] = useState(false);
  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList,
  );
  const unqualifiedReasons = useSelector(
    (state) => state.leadListingReducer.unqualifiedReasons,
  );

  useEffect(() => {
    const payload =
      fieldName === "fullname"
        ? {
            [fieldName]: defaultValue?.fullname,
            firstname: defaultValue?.firstname,
            lastname: defaultValue?.lastname,
          }
        : fieldName === "status"
          ? {
              status: leadStatusListing?.find(
                (i) => i?.id === defaultValue?.status,
              )?.text,
              lostReason: defaultValue?.lostReason,
              unqualifiedReason: defaultValue?.unqualifiedReason,
              comments: defaultValue?.comments,
              unqualifiedReasonsOthers: defaultValue?.unqualifiedReasonsOthers,
            }
          : ["phoneNo", "mobile"].includes(fieldName)
            ? (() => {
                const rawVal = String(defaultValue || "");
                const phoneVal = rawVal ? rawVal.split("/")?.[0] : "";
                const details = formatAndExtractCountryCode(phoneVal);
                return {
                  prefix: details?.countryLabel,
                  [fieldName]: details?.numberWithoutCountryCode,
                };
              })()
            : { [fieldName]: defaultValue };
    form.setFieldsValue(payload);
    setStatus(
      leadStatusListing?.find((d) => d?.id === defaultValue?.status)?.text,
    );
    setReason(defaultValue?.unqualifiedReason);
    if (fieldName === "status") {
      setStatus(
        leadStatusListing?.find((i) => i?.id === defaultValue?.status)?.text,
      );
    }
  }, [form, fieldName, defaultValue, leadStatusListing]);

  const onFinish = async (values) => {
    const cleanedValues = Object.fromEntries(
      Object.entries(
        fieldName === "fullname"
          ? {
              ...values,
              [fieldName]: `${values?.firstname || ""} ${values?.lastname || ""}`.trim(),
            }
          : fieldName === "status"
            ? {
                ...values,
                status: leadStatusListing?.find(
                  (i) => i.text === values?.status,
                )?.id,
              }
            : values,
      ).map(([key, value]) => [
        key,
        typeof value === "string" ? value?.trim().replace(/\s+/g, " ") : value,
      ]),
    );
    const defaultFieldValue =
      fieldName === "fullname"
        ? defaultValue[fieldName]?.trim().replace(/\s+/g, " ")
        : defaultValue;
    if (defaultFieldValue !== cleanedValues[fieldName]) {
      const res = await dispatch(
        updateLeadDetails(
          fieldName === "companyName"
            ? {
                id,
                userId,
                website: values?.website,
                ...cleanedValues,
              }
            : ["phoneNo", "mobile"].includes(fieldName)
              ? {
                  id,
                  userId,
                  ...cleanedValues,
                  [fieldName]:
                    "+" +
                    countries?.find((d) => d?.label === cleanedValues?.prefix)
                      ?.code +
                    " " +
                    cleanedValues[fieldName],
                }
              : {
                  id,
                  userId,
                  ...cleanedValues,
                  unqualifiedReason: {
                    id: unqualifiedReasons?.find(
                      (d) => d?.reason === cleanedValues?.unqualifiedReason,
                    )?.id,
                    reason: cleanedValues?.unqualifiedReason,
                  },
                },
        ),
      );
      if (res?.status === 200) {
        showAlert(
          "success",
          `${res?.data?.firstname} ${res?.data?.lastname} Lead updated successfully!`,
        );
        callback && callback(res?.data);
        const allParams = {};
        for (const [key, value] of searchParams.entries()) {
          allParams[key] = value;
        }
        dispatch(
          getExecutiveEmployeeList({
            ...allParams,
            page: allParams?.page ? allParams?.page - 1 : 0,
          }),
        );
        handleCancel();
        setIsChanged(false);
        form.resetFields();
        setStatus();
      } else {
        if (res?.response?.status === 403) {
          showAlert(
            "warning",
            "Permission Denied",
            "You do not have permission to update lead.",
          );
          handleCancel();
          setIsChanged(false);
          form.resetFields();
          setStatus();
        } else {
          showAlert("error", res?.response?.data?.title);
          setError(res?.response?.data?.title);
        }
      }
    }
  };

  return (
    <Form
      form={form}
      initialValues={
        fieldName === "fullname"
          ? {
              [fieldName]: defaultValue?.fullname,
              firstname: defaultValue?.firstname,
              lastname: defaultValue?.lastname,
            }
          : {
              [fieldName]: defaultValue,
            }
      }
      layout="vertical"
      onFinish={onFinish}
      onValuesChange={() => {
        setError();
        setIsChanged(true);
      }}
    >
      <div className="tw-p-6">
        {error && <p className="text-error text-center pb-2">{error}</p>}
        {["fullname", "phoneNo", "mobile", "emailId", "email"]?.includes(
          fieldName,
        ) ? (
          <Form.Item
            label={fieldName === "fullname" ? "First Name" : title}
            name={fieldName === "fullname" ? "firstname" : fieldName}
            normalize={(value) =>
              ["phoneNo", "mobile"].includes(fieldName)
                ? value.replace(/[^\d+\- ()]/g, "")
                : value?.replace(/^\s+/, "")
            }
            rules={[
              { required: true, message: "This field is required" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (["phoneNo", "mobile"].includes(fieldName)) {
                    const digitCount = (value.match(/\d/g) || []).length;

                    if (value.length > 20) {
                      return Promise.reject(
                        `Please enter a valid ${fieldName === "mobile" ? "mobile" : "phone"} number`,
                      );
                    }

                    if (digitCount > 15) {
                      return Promise.reject(
                        `Please enter a valid ${fieldName === "mobile" ? "mobile" : "phone"} number`,
                      );
                    }

                    return Promise.resolve();
                  } else if (["emailId", "email"].includes(fieldName)) {
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                      return Promise.reject(
                        "Please enter a valid email address",
                      );
                    }
                    return Promise.resolve();
                  } else {
                    return Promise.resolve();
                  }
                },
              },
            ]}
          >
            {["phoneNo", "mobile"].includes(fieldName) ? (
              <Input
                className="common-input"
                addonBefore={prefixSelector()}
                maxLength={20}
                onChange={(e) => handleMobileChange(e.target.value, form)}
              />
            ) : (
              <Input className="common-input" />
            )}
          </Form.Item>
        ) : fieldName === "status" ? (
          <>
            <Form.Item
              label={title}
              name={fieldName}
              normalize={(value) => value?.replace(/^\s+/, "")}
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Select
                value={reason}
                placeholder="Select Status"
                options={leadStatusListing?.map((d) => ({
                  label: d.text,
                  value: d.text,
                }))}
                onChange={(value) => {
                  form.setFieldsValue({
                    [fieldName]: value,
                    unqualifiedReason: null,
                    unqualifiedReasonsOthers: null,
                  });
                  setIsChanged(true);
                  setStatus(value);
                }}
              />
            </Form.Item>
            {status === "Closed Lost" && (
              <>
                <Form.Item
                  name="lostReason"
                  label="Lost Reason"
                  rules={[{ required: true, message: "Please add a reason" }]}
                >
                  <Input className="common-input" placeholder="Enter reason" />
                </Form.Item>

                <Form.Item name="comments" label="Comments">
                  <TextArea
                    className="common-input"
                    rows={3}
                    placeholder="Enter comments"
                  />
                </Form.Item>
              </>
            )}

            {status === "Un-Qualified" && (
              <>
                <Form.Item
                  name="unqualifiedReason"
                  label="Reason for Unqualification"
                  rules={[
                    { required: true, message: "Please select a reason" },
                  ]}
                >
                  <Select
                    placeholder="Select reason"
                    options={unqualifiedReasons?.map((d) => ({
                      label: d.reason,
                      value: d.reason,
                    }))}
                    onChange={(value) => {
                      form.setFieldsValue({
                        unqualifiedReason: value,
                        unqualifiedReasonsOthers: undefined,
                      });
                      setIsChanged(true);
                      setReason(value);
                    }}
                  />
                </Form.Item>

                {reason === "Others" && (
                  <Form.Item
                    name="unqualifiedReasonsOthers"
                    label="Add Reason"
                    rules={[{ required: true, message: "Please add a reason" }]}
                  >
                    <TextArea
                      className="common-input"
                      rows={3}
                      placeholder="Enter unqualification reason"
                    />
                  </Form.Item>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <Form.Item
              label={title}
              name={fieldName}
              normalize={(value) => value?.replace(/^\s+/, "")}
              rules={[{ required: true, message: "This field is required" }]}
            >
              <PlaceAutocomplete
                onPlaceSelect={(v) => {
                  form.setFieldsValue({
                    [fieldName]: v?.name,
                    website: v?.website,
                  });
                  setIsChanged(true);
                }}
              />
            </Form.Item>
            <Form.Item name="website" label="Website" className="d-none">
              <Input className="common-input" />
            </Form.Item>
          </>
        )}

        {fieldName === "fullname" && (
          <Form.Item
            label="Last Name"
            name="lastname"
            normalize={(value) => value?.replace(/^\s+/, "")}
            rules={[{ required: true, message: "This field is required" }]}
          >
            <Input />
          </Form.Item>
        )}
      </div>
      <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
        <Button
          className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
          onClick={() => {
            handleCancel();
            setIsChanged(false);
            form.resetFields();
            setStatus();
          }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={!isChanged}
          htmlType="submit"
          className="!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
        >
          Update
        </Button>
      </div>
    </Form>
  );
};
